// "Prior work" — the deal-context section of the Detail panel: similar
// companies the firm has seen (precedents), same-sector history, and the
// firm's articulated knowledge (themes + ask). Fetches screen-locally.

import { useEffect, useState } from "react";
import { askKnowledge, dismissPrecedent, getDealContext } from "../api";
import type { DealContext, PrecedentCard, SectorRow } from "../types";
import { Ic } from "../components/Icon";
import { Logo } from "../components/Logo";
import { Md } from "../components/Md";

const STATUS_COLOR: Record<string, string> = {
  invested: "var(--pos)", passed: "var(--neg)", review: "var(--warn)",
  new: "var(--ink-3)", rejected: "var(--ink-4)",
};

interface AskAnswer {
  answer: string[];
  sources: { type: string; title: string; slug?: string }[];
}

export function fmtMonth(raw: string | null | undefined): string {
  if (!raw) return "";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

export function StatusChip({ status }: { status: string }) {
  return (
    <span style={{
      fontSize: 10.5, fontWeight: 600, textTransform: "capitalize",
      color: STATUS_COLOR[status] ?? "var(--ink-3)",
    }}>
      {status}
    </span>
  );
}

function Sub({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 600, textTransform: "uppercase",
      letterSpacing: "0.08em", color: "var(--ink-3)", margin: "12px 0 7px",
    }}>
      {children}
    </div>
  );
}

export function CompanyRow({ co, domain, meta, right, onOpen }: {
  co: string; domain?: string | null; meta?: React.ReactNode;
  right?: React.ReactNode; onOpen?: () => void;
}) {
  return (
    <div
      onClick={onOpen}
      style={{
        display: "flex", alignItems: "center", gap: 9, padding: "6px 10px",
        background: "var(--bg)", border: "1px solid var(--border)",
        borderRadius: 7, cursor: onOpen ? "pointer" : "default",
      }}
    >
      <Logo domain={domain ?? undefined} co={co} size={24} radius={5} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: 12.5, fontWeight: 500 }}>{co}</span>
        {meta}
      </div>
      {right}
    </div>
  );
}

function PrecedentRow({ card, onOpen, onDismiss, dismissing }: {
  card: PrecedentCard;
  onOpen?: () => void;
  onDismiss: () => void;
  dismissing: boolean;
}) {
  return (
    <CompanyRow
      co={card.co}
      domain={null}
      onOpen={onOpen}
      meta={
        <div style={{ fontSize: 11, color: "var(--ink-3)", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          {card.pin === "domain" && (
            <span style={{ color: "var(--accent)", fontWeight: 600 }}>Same company · seen before</span>
          )}
          {card.pin === "founder" && (
            <span style={{ color: "var(--accent)", fontWeight: 600 }}>
              Repeat founder{card.pinNote ? ` · ${card.pinNote}` : ""}
            </span>
          )}
          {card.stage && <span>{card.stage}</span>}
          {card.date && <span>· {fmtMonth(card.date)}</span>}
          {card.reason && <span style={{ fontStyle: "italic" }}>· "{card.reason}"</span>}
          {card.anti && (card.anti.then || card.anti.mult) && (
            <span style={{ color: "var(--warn)", fontWeight: 600 }}>
              · later {card.anti.then}{card.anti.mult ? ` (${card.anti.mult})` : ""}
            </span>
          )}
        </div>
      }
      right={
        <>
          <StatusChip status={card.status} />
          {card.score != null && (
            <span style={{
              fontFamily: "var(--mono)", fontSize: 11.5, fontWeight: 700,
              color: "var(--ink-2)", background: "var(--raised)",
              padding: "1px 6px", borderRadius: 4,
            }}>
              {card.score}
            </span>
          )}
          <button
            title="Dismiss — not a real match"
            onClick={(e) => { e.stopPropagation(); onDismiss(); }}
            disabled={dismissing}
            style={{
              border: "none", background: "none", cursor: "pointer",
              color: "var(--ink-4)", padding: 2, lineHeight: 0,
            }}
          >
            <Ic name="x" size={11} />
          </button>
        </>
      }
    />
  );
}

export function PriorWork({ dealId, onOpenDeal, onPeople }: {
  dealId: string;
  onOpenDeal?: (id: string) => void;
  /** Lifts the graph's people for this deal to the Detail header chips. */
  onPeople?: (people: DealContext["people"]) => void;
}) {
  const [ctx, setCtx] = useState<DealContext | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dismissing, setDismissing] = useState<string | null>(null);
  const [askBusy, setAskBusy] = useState(false);
  const [askResult, setAskResult] = useState<AskAnswer | null>(null);

  useEffect(() => {
    let live = true;
    setCtx(null); setError(null); setAskResult(null);
    getDealContext(dealId)
      .then((c) => {
        if (!live) return;
        setCtx(c);
        onPeople?.(c.people ?? []);
      })
      .catch((e) => { if (live) setError(String((e as Error).message || e)); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  const dismiss = async (slug: string) => {
    if (!ctx || dismissing) return;
    setDismissing(slug);
    try {
      await dismissPrecedent(dealId, slug);
      setCtx({ ...ctx, precedents: ctx.precedents.filter((c) => c.slug !== slug) });
    } finally {
      setDismissing(null);
    }
  };

  const ask = async (prompt: string) => {
    if (askBusy) return;
    setAskBusy(true);
    try {
      setAskResult(await askKnowledge<AskAnswer>(prompt));
    } catch (e) {
      setError(String((e as Error).message || e));
    } finally {
      setAskBusy(false);
    }
  };

  const sector = ctx?.sameSector.sector ?? null;

  return (
    <>
      <div style={{ padding: "10px 22px 6px" }}>
        <div className="h-section">
          <h3>Prior work</h3>
          <span className="eyebrow" style={{ color: "var(--ink-3)", textTransform: "none", letterSpacing: 0 }}>
            · similar companies · sector history · the firm's view
          </span>
        </div>
      </div>

      <div style={{ padding: "0 22px 14px" }}>
        {error && <div style={{ fontSize: 12, color: "var(--neg)" }}>{error}</div>}
        {!ctx && !error && (
          <div style={{ fontSize: 12, color: "var(--ink-3)" }}>Looking up prior work…</div>
        )}

        {ctx && (
          <>
            {/* Similar companies */}
            <Sub>Similar companies{ctx.precedents.length > 0 ? ` · ${ctx.precedents.length}` : ""}</Sub>
            {ctx.precedents.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {ctx.precedents.map((card) => (
                  <PrecedentRow
                    key={card.slug}
                    card={card}
                    onOpen={onOpenDeal ? () => onOpenDeal(card.slug) : undefined}
                    onDismiss={() => dismiss(card.slug)}
                    dismissing={dismissing === card.slug}
                  />
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: "var(--ink-3)" }}>
                No precedents yet — Prism gets smarter as more history lands in the archive.
              </div>
            )}

            {/* Same sector */}
            {sector && (
              <>
                <Sub>
                  {sector} · {ctx.sameSector.seen} seen · {ctx.sameSector.passed} passed · {ctx.sameSector.invested} invested
                </Sub>
                {ctx.sameSector.rows.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {ctx.sameSector.rows.map((row: SectorRow) => (
                      <CompanyRow
                        key={row.slug}
                        co={row.co}
                        onOpen={onOpenDeal ? () => onOpenDeal(row.slug) : undefined}
                        meta={
                          <div style={{ fontSize: 11, color: "var(--ink-3)" }}>
                            {fmtMonth(row.date)}{row.reason ? ` · "${row.reason}"` : ""}
                          </div>
                        }
                        right={
                          <>
                            <StatusChip status={row.status} />
                            {row.score != null && (
                              <span style={{ fontFamily: "var(--mono)", fontSize: 11.5, color: "var(--ink-3)" }}>
                                {row.score}
                              </span>
                            )}
                          </>
                        }
                      />
                    ))}
                  </div>
                ) : (
                  <div style={{
                    fontSize: 12, color: "var(--accent)", fontWeight: 500,
                    padding: "8px 12px", background: "var(--accent-soft)", borderRadius: 7,
                  }}>
                    First {sector} company the firm has seen.
                  </div>
                )}
              </>
            )}

            {/* Signal axis — existing backers, with fund overlap */}
            {ctx.signal.backers.length > 0 && (
              <>
                <Sub>Backers named in the material</Sub>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {ctx.signal.backers.map((b) => (
                    <div key={b.name} style={{
                      padding: "7px 12px", background: "var(--bg)",
                      border: "1px solid var(--border)", borderRadius: 7,
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 12.5, fontWeight: 600 }}>{b.name}</span>
                        {b.role && (
                          <span style={{
                            fontSize: 9.5, fontWeight: 600, textTransform: "capitalize",
                            color: "var(--accent)", background: "var(--accent-soft)",
                            padding: "1px 6px", borderRadius: 8,
                          }}>
                            {b.role}
                          </span>
                        )}
                        {b.round && <span style={{ fontSize: 11, color: "var(--ink-3)" }}>{b.round}</span>}
                        {b.batch && <span style={{ fontSize: 11, color: "var(--ink-3)" }}>· {b.batch}</span>}
                      </div>
                      {b.alsoBacked.length > 0 && (
                        <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 3, display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
                          <span>Also backed deals we've seen:</span>
                          {b.alsoBacked.map((a) => (
                            <span
                              key={a.slug}
                              onClick={onOpenDeal ? () => onOpenDeal(a.slug) : undefined}
                              style={{
                                color: "var(--accent)", fontWeight: 500,
                                cursor: onOpenDeal ? "pointer" : "default",
                              }}
                            >
                              {a.co}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Signal axis — the competitive set */}
            {(ctx.signal.competitors.length > 0 || ctx.signal.namedBy.length > 0) && (
              <>
                <Sub>Competitive set</Sub>
                {ctx.signal.competitors.length > 0 && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
                    {ctx.signal.competitors.map((c) => (
                      <span
                        key={c.co}
                        onClick={c.slug && onOpenDeal ? () => onOpenDeal(c.slug!) : undefined}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 6,
                          fontSize: 11.5, padding: "3px 10px", borderRadius: 10,
                          background: c.slug ? "var(--accent-soft)" : "var(--raised)",
                          border: "1px solid var(--border)",
                          color: "var(--ink-2)",
                          cursor: c.slug && onOpenDeal ? "pointer" : "default",
                        }}
                        title={c.slug ? "We've seen this company — open the deal" : "Named in the material; not in our pipeline"}
                      >
                        {c.co}
                        {c.status && <StatusChip status={c.status} />}
                      </span>
                    ))}
                  </div>
                )}
                {ctx.signal.namedBy.length > 0 && (
                  <div style={{ fontSize: 11, color: "var(--ink-3)", display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
                    <span>Named as a competitor by</span>
                    {ctx.signal.namedBy.map((n) => (
                      <span
                        key={n.slug}
                        onClick={onOpenDeal ? () => onOpenDeal(n.slug) : undefined}
                        style={{ color: "var(--accent)", fontWeight: 500, cursor: onOpenDeal ? "pointer" : "default" }}
                      >
                        {n.co}
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Firm knowledge */}
            {(ctx.knowledge.themes.length > 0 || ctx.knowledge.askPrompt) && (
              <>
                <Sub>The firm's view</Sub>
                {ctx.knowledge.themes.map((t) => (
                  <div key={t.id} style={{
                    padding: "8px 12px", background: "var(--bg)",
                    border: "1px solid var(--border)", borderRadius: 7, marginBottom: 6,
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600, flex: 1 }}>{t.title}</span>
                      <span style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--ink-3)" }}>
                        {t.conviction}% conviction
                      </span>
                    </div>
                    {t.keyInsight && (
                      <div style={{ fontSize: 11.5, color: "var(--ink-2)", marginTop: 3, fontStyle: "italic" }}>
                        <Md text={t.keyInsight} />
                      </div>
                    )}
                  </div>
                ))}
                {ctx.knowledge.askPrompt && !askResult && (
                  <button
                    onClick={() => ask(ctx.knowledge.askPrompt!)}
                    disabled={askBusy}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      fontSize: 12, fontWeight: 500, color: "var(--accent)",
                      background: "none", border: "1px solid var(--border)",
                      borderRadius: 7, padding: "6px 12px", cursor: "pointer",
                    }}
                  >
                    <Ic name="sparkle" size={12} />
                    {askBusy ? "Asking…" : `Ask: ${ctx.knowledge.askPrompt}`}
                  </button>
                )}
                {askResult && (
                  <div style={{
                    padding: "10px 14px", background: "var(--accent-soft)",
                    borderLeft: "3px solid var(--accent)", borderRadius: "0 6px 6px 0",
                  }}>
                    {askResult.answer.map((p, i) => (
                      <div key={i} style={{ fontSize: 12.5, lineHeight: 1.6, color: "var(--ink-2)", marginBottom: 6 }}>
                        <Md text={p} />
                      </div>
                    ))}
                    {askResult.sources.length > 0 && (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                        {askResult.sources.map((src, i) => (
                          <span
                            key={i}
                            onClick={src.slug && onOpenDeal ? () => onOpenDeal(src.slug!) : undefined}
                            style={{
                              fontSize: 10.5, padding: "2px 8px", borderRadius: 10,
                              background: "var(--surface)", border: "1px solid var(--border)",
                              color: "var(--ink-2)",
                              cursor: src.slug && onOpenDeal ? "pointer" : "default",
                            }}
                          >
                            {src.title}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}
